import { Icon } from '@fluentui/react';
import { forwardRef, HTMLAttributes, Ref } from 'react';

import { SafeExternalLink } from '@shared/lib/ui/components-alpha/SafeExternalLink';

import { IComponent, IconName } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { Image } from '../Image/Image';
import { Link } from '../Link/Link';
import { linkStylesGhost } from '../Link/Link.styles';
import { emptyMessageStylesDefault, IEmptyMessageStyles } from './EmptyMessage.styles';

export interface IEmptyMessageProps extends IComponent<IEmptyMessageStyles>, HTMLAttributes<HTMLDivElement> {
  imagePath?: string;
  iconName?: string;
  title?: string;
  message?: string;
  linkText?: string;
  linkHref?: string;
  linkOnClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLElement>) => void;
}

interface IEmptyMessageClassNames {
  root: string;
  body: string;
  image: string;
  title: string;
  message: string;
  link: string;
}

export const EmptyMessage = forwardRef((props: IEmptyMessageProps, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    styles,
    imagePath,
    iconName,
    title,
    message = 'No data',
    linkText,
    linkHref,
    linkOnClick,
    ...elementProps
  } = props;
  const classNames = useClassNames<Partial<IEmptyMessageStyles>, IEmptyMessageClassNames>(
    'EmptyMessage',
    emptyMessageStylesDefault,
    styles,
    className
  );
  return (
    <div {...elementProps} ref={ref} className={classNames.root} data-test={props['data-test'] || 'empty-message'}>
      <div className={classNames.body}>
        <div className={classNames.image}>
          {imagePath && <Image src={imagePath} />}
          {(iconName || !imagePath) && <Icon iconName={iconName || IconName.Stack} />}
        </div>
        {title && <div className={classNames.title}>{title}</div>}
        <div className={classNames.message}>{message}</div>
        {(linkHref || linkOnClick) && (
          <div className={classNames.link}>
            {linkHref ? (
              <SafeExternalLink href={linkHref} onClick={linkOnClick}>
                {linkText}
              </SafeExternalLink>
            ) : (
              <Link styles={linkStylesGhost} onClick={linkOnClick}>
                {linkText}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
